import type { WindowStatus } from './windows';

/**
 * Store grades (Phase 6a; SPLIT IN TWO by owner ruling 2026-10-19,
 * docs/dashboards.md): two 0–100 numbers per store, each with its own
 * color band, averaged per district — so it is clear at a glance whether a
 * site is in good shape but late, on time but failing, or both.
 *
 *   Timeliness = 60% audits on time + 40% open tasks not overdue
 *                (owner ruling 2026-10-19)
 *   Quality    = average score of the store's last 3 audits
 *
 * - Audits on time, per responsible-party clock the store has started:
 *   current 100 · due soon 100 (only lateness costs points — owner ruling
 *   2026-10-22) · overdue 40 on its first day, falling evenly to 0 by day
 *   21. Averaged across clocks.
 * - Open tasks: share of the store's open tasks that are NOT overdue. A
 *   reopened task counts like any other until its new due date passes
 *   (owner ruling 2026-10-22 — it used to count half, so reopening to ask
 *   for a photo cost the store points). No open tasks = 100.
 * - Quality: the most recent (up to 3) submitted DM/Auditor audits that
 *   have a score, from the last ~13 months — a store team's self-audit and
 *   a score-less form (e.g. a DM Visit) never count.
 *
 * A store that has never been audited gets NO timeliness grade (the
 * compliance clock starts at the first audit — owner ruling 2026-10-16),
 * and a store with no scored audit gets no quality grade.
 */

export type GradeKind = 'timeliness' | 'quality';

/** Timeliness parts (sum 100). */
export const TIMELINESS_WEIGHTS = { onTime: 60, tasks: 40 } as const;
/** Quality = average of this many most recent scored audits… */
export const QUALITY_AUDITS = 3;
/**
 * …submitted within this many days (owner ruling 2026-10-19: an older audit
 * never sets today's Quality). Also the window Home, district pages and
 * Analytics read (data/audits.ts RECENT_AUDIT_DAYS), so every screen agrees.
 */
export const QUALITY_MAX_AGE_DAYS = 400;

/** Color bands (owner ruling 2026-10-17), the same for both grades: 80+, 60–79, below 60. */
export const BAND_GREEN = 80;
export const BAND_YELLOW = 60;

/** "Due soon" is a warning, not lateness (owner ruling 2026-10-22; was 70). */
export const DUE_SOON_POINTS = 100;
export const OVERDUE_START_POINTS = 40;
/** Overdue points reach 0 on this day overdue. */
export const OVERDUE_ZERO_DAY = 21;

export type GradeBand = 'green' | 'yellow' | 'red';

export const GRADE_NAME: Record<GradeKind, string> = {
  timeliness: 'Timeliness',
  quality: 'Quality',
};

/** What each band means for each grade. */
export const BAND_LABEL: Record<GradeKind, Record<GradeBand, string>> = {
  timeliness: { green: 'On time', yellow: 'Slipping', red: 'Late' },
  quality: { green: 'Good', yellow: 'Fair', red: 'Failing' },
};

/** Band of the DISPLAYED (rounded) grade, so a chip reading "80" is never yellow. */
export function gradeBand(grade: number): GradeBand {
  const shown = Math.round(grade);
  if (shown >= BAND_GREEN) return 'green';
  if (shown >= BAND_YELLOW) return 'yellow';
  return 'red';
}

/**
 * Points for one clock's audit window. `daysOverdue` counts from 1 on the
 * first overdue day. 'never' isn't scored (returns null).
 */
export function onTimePoints(status: WindowStatus, daysOverdue = 0): number | null {
  switch (status) {
    case 'current':
      return 100;
    case 'due_soon':
      return DUE_SOON_POINTS;
    case 'overdue': {
      const d = Math.max(1, Math.floor(daysOverdue));
      if (d >= OVERDUE_ZERO_DAY) return 0;
      // Day 1 → 40, evenly down to 0 on day 21.
      return (OVERDUE_START_POINTS * (OVERDUE_ZERO_DAY - d)) / (OVERDUE_ZERO_DAY - 1);
    }
    default:
      return null;
  }
}

export interface GradeTask {
  overdue: boolean;
}

/** 0–100: share of open tasks not overdue (reopened ones too). No tasks = 100. */
export function taskHygiene(tasks: readonly GradeTask[]): number {
  if (tasks.length === 0) return 100;
  const points = tasks.filter((t) => !t.overdue).length;
  return (100 * points) / tasks.length;
}

/** A usable 0–100 score, or null (missing, not a number). */
export function cleanScore(score: unknown): number | null {
  return typeof score === 'number' && Number.isFinite(score) ? Math.max(0, Math.min(100, score)) : null;
}

export interface GradeInputs {
  /** One entry per responsible-party clock that applies to the store. */
  windows: readonly { status: WindowStatus; daysOverdue: number }[];
  tasks: readonly GradeTask[];
  /**
   * Scores of the store's DM/Auditor audits, NEWEST FIRST (any order of
   * nulls — they're skipped); only the first QUALITY_AUDITS usable ones count.
   */
  recentScores: readonly (number | null)[];
}

export interface StoreGrade {
  /** 0–100, or null = never audited (no grade). */
  timeliness: number | null;
  /** 0–100, or null = no scored audit yet. */
  quality: number | null;
  /** Timeliness parts: on-time points (null = no clock started) and task hygiene. */
  onTime: number | null;
  tasks: number;
  /** The scores averaged into quality, newest first. */
  scores: number[];
}

export function storeGrade(input: GradeInputs): StoreGrade {
  const points = input.windows
    .map((w) => onTimePoints(w.status, w.daysOverdue))
    .filter((p): p is number => p !== null);
  const onTime = points.length ? points.reduce((a, b) => a + b, 0) / points.length : null;
  const tasks = taskHygiene(input.tasks);
  const timeliness =
    onTime === null
      ? null
      : (onTime * TIMELINESS_WEIGHTS.onTime + tasks * TIMELINESS_WEIGHTS.tasks) / 100;
  const scores = input.recentScores
    .map(cleanScore)
    .filter((s): s is number => s !== null)
    .slice(0, QUALITY_AUDITS);
  const quality = scores.length ? scores.reduce((a, b) => a + b, 0) / scores.length : null;
  return { timeliness, quality, onTime, tasks, scores };
}

/** The store grade of the given kind. */
export const gradeOf = (g: StoreGrade, kind: GradeKind): number | null =>
  kind === 'timeliness' ? g.timeliness : g.quality;

/** Display: whole numbers ("86"); no grade shows "—". */
export function formatGrade(grade: number | null): string {
  return grade === null ? '—' : String(Math.round(grade));
}

/** An empty (not-yet-graded) store grade. */
export const NO_GRADE: StoreGrade = { timeliness: null, quality: null, onTime: null, tasks: 100, scores: [] };
